import type { UiPalette } from '@theme/uiPalette.types'
import { PALETTE_BLUE } from '@theme/palettes/paletteBlue'
import { deriveAccentFromMain } from '@theme/deriveAccentPalette'

export const THEME_PRESET_STORAGE_KEY = 'auth-theme-preset-v1'

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

/**
 * Bảng theo theme — header đậm hơn tint trắng mỏng, hover/selected nhuộm nhẹ.
 * Accent mạnh để ở shell (topbar/sidebar).
 * Màu ô lưới khớp `banhang-frontend` ACCENT_PRESETS.
 */
const tableTone = (opts: {
  headBg: string
  headBorder: string
  headText: string
  level2HeadBg: string
  level2HeadBorder: string
  level2HeadText: string
  rowHoverBg: string
  rowSelectedBg: string
  rowSelectedHoverBg: string
}) =>
  ({
    headBg: opts.headBg,
    headBorder: opts.headBorder,
    headText: opts.headText,
    rowHoverBg: opts.rowHoverBg,
    rowSelectedBg: opts.rowSelectedBg,
    rowSelectedHoverBg: opts.rowSelectedHoverBg,
    nestedHeadBg: opts.level2HeadBg,
    nestedHeadText: opts.level2HeadText,
    nestedHeadBorder: opts.level2HeadBorder,
    level2HeadBg: opts.level2HeadBg,
    level2HeadText: opts.level2HeadText,
    level2HeadBorder: opts.level2HeadBorder,
  }) as const

const mergePalette = (base: UiPalette, patch: DeepPartial<UiPalette>): UiPalette => {
  const out = JSON.parse(JSON.stringify(base)) as UiPalette
  const apply = (tgt: Record<string, unknown>, src: Record<string, unknown>): void => {
    for (const key of Object.keys(src)) {
      const pv = src[key]
      if (pv === undefined) continue
      const cur = tgt[key]
      if (pv !== null && typeof pv === 'object' && !Array.isArray(pv) && typeof cur === 'object' && cur !== null && !Array.isArray(cur)) {
        apply(cur as Record<string, unknown>, pv as Record<string, unknown>)
      } else if (typeof pv === 'string') {
        tgt[key] = pv
      }
    }
  }
  apply(out as unknown as Record<string, unknown>, patch as Record<string, unknown>)
  // Các preset chỉ đổi accent; nền trang/disabled luôn trung tính và shell
  // phải lấy tint từ chính preset thay vì kế thừa màu blue mặc định.
  out.shell.sidebarHoverBg = out.primary.surface
  out.shell.sidebarSectionBg = out.primary.surface
  out.shell.sidebarSectionBar = out.primary.surfaceBorder
  return out
}

/** Khớp banhang: blue / navy / green / purple / red / slate / teal / orange / pink */
const PRESET_PARTIALS: Record<string, DeepPartial<UiPalette>> = {
  slate: {
    primary: {
      main: '#334155',
      dark: '#1e293b',
      light: '#64748b',
      surface: '#f1f5f9',
      surfaceBorder: '#cbd5e1',
      hover: 'rgba(51, 65, 85, 0.08)',
    },
    shell: {
      headerSolid: '#334155',
      sidebarActiveBg: '#f1f5f9',
      sidebarActiveBgHover: '#e2e8f0',
      sidebarActiveText: '#1e293b',
      sidebarActiveIcon: '#334155',
      sidebarActiveBar: '#334155',
      sidebarSectionText: '#0f172a',
    },
    table: tableTone({
      headBg: '#e8ebef',
      headBorder: '#d6dbe3',
      headText: '#333333',
      level2HeadBg: '#f1f5f9',
      level2HeadBorder: '#cbd5e1',
      level2HeadText: '#1e293b',
      rowHoverBg: '#f8fafc',
      rowSelectedBg: '#e2e8f0',
      rowSelectedHoverBg: '#cbd5e1',
    }),
  },
  green: {
    primary: {
      main: '#15803d',
      dark: '#166534',
      light: '#4ade80',
      surface: '#f0fdf4',
      surfaceBorder: '#bbf7d0',
      hover: 'rgba(21, 128, 61, 0.08)',
    },
    shell: {
      headerSolid: '#15803d',
      sidebarActiveBg: '#f0fdf4',
      sidebarActiveBgHover: '#dcfce7',
      sidebarActiveText: '#166534',
      sidebarActiveIcon: '#16a34a',
      sidebarActiveBar: '#16a34a',
      sidebarSectionText: '#14532d',
    },
    table: tableTone({
      headBg: '#e6f4eb',
      headBorder: '#d0e6d8',
      headText: '#333333',
      level2HeadBg: '#f0fdf4',
      level2HeadBorder: '#bbf7d0',
      level2HeadText: '#166534',
      rowHoverBg: '#f6faf7',
      rowSelectedBg: '#dcfce7',
      rowSelectedHoverBg: '#bbf7d0',
    }),
  },
  purple: {
    primary: {
      main: '#7c3aed',
      dark: '#5b21b6',
      light: '#a78bfa',
      surface: '#f5f3ff',
      surfaceBorder: '#ddd6fe',
      hover: 'rgba(124, 58, 237, 0.08)',
    },
    shell: {
      headerSolid: '#7c3aed',
      sidebarActiveBg: '#f5f3ff',
      sidebarActiveBgHover: '#ede9fe',
      sidebarActiveText: '#5b21b6',
      sidebarActiveIcon: '#7c3aed',
      sidebarActiveBar: '#7c3aed',
      sidebarSectionText: '#4c1d95',
    },
    table: tableTone({
      headBg: '#efeafb',
      headBorder: '#ddd4f3',
      headText: '#333333',
      level2HeadBg: '#f5f3ff',
      level2HeadBorder: '#ddd6fe',
      level2HeadText: '#5b21b6',
      rowHoverBg: '#f8f7fb',
      rowSelectedBg: '#ede9fe',
      rowSelectedHoverBg: '#ddd6fe',
    }),
  },
  coral: {
    primary: {
      main: '#dc2626',
      dark: '#b91c1c',
      light: '#f87171',
      surface: '#fef2f2',
      surfaceBorder: '#fecaca',
      hover: 'rgba(220, 38, 38, 0.08)',
    },
    shell: {
      headerSolid: '#dc2626',
      sidebarActiveBg: '#fef2f2',
      sidebarActiveBgHover: '#fee2e2',
      sidebarActiveText: '#b91c1c',
      sidebarActiveIcon: '#dc2626',
      sidebarActiveBar: '#dc2626',
      sidebarSectionText: '#991b1b',
    },
    table: tableTone({
      headBg: '#fbe8e8',
      headBorder: '#f0d0d0',
      headText: '#333333',
      level2HeadBg: '#fef2f2',
      level2HeadBorder: '#fecaca',
      level2HeadText: '#b91c1c',
      rowHoverBg: '#faf7f8',
      rowSelectedBg: '#fee2e2',
      rowSelectedHoverBg: '#fecaca',
    }),
  },
  sage: {
    primary: {
      main: '#64748b',
      dark: '#475569',
      light: '#94a3b8',
      surface: '#f8fafc',
      surfaceBorder: '#e2e8f0',
      hover: 'rgba(100, 116, 139, 0.08)',
    },
    shell: {
      headerSolid: '#64748b',
      sidebarActiveBg: '#f8fafc',
      sidebarActiveBgHover: '#f1f5f9',
      sidebarActiveText: '#475569',
      sidebarActiveIcon: '#64748b',
      sidebarActiveBar: '#64748b',
      sidebarSectionText: '#334155',
    },
    table: tableTone({
      headBg: '#eceff3',
      headBorder: '#d8dde5',
      headText: '#333333',
      level2HeadBg: '#f8fafc',
      level2HeadBorder: '#e2e8f0',
      level2HeadText: '#475569',
      rowHoverBg: '#f8fafc',
      rowSelectedBg: '#f1f5f9',
      rowSelectedHoverBg: '#e2e8f0',
    }),
  },
  teal: {
    primary: {
      main: '#0d9488',
      dark: '#0f766e',
      light: '#2dd4bf',
      surface: '#f0fdfa',
      surfaceBorder: '#99f6e4',
      hover: 'rgba(13, 148, 136, 0.08)',
    },
    shell: {
      headerSolid: '#0d9488',
      sidebarActiveBg: '#f0fdfa',
      sidebarActiveBgHover: '#ccfbf1',
      sidebarActiveText: '#0f766e',
      sidebarActiveIcon: '#14b8a6',
      sidebarActiveBar: '#14b8a6',
      sidebarSectionText: '#115e59',
    },
    table: tableTone({
      headBg: '#e5f7f5',
      headBorder: '#cfe9e5',
      headText: '#333333',
      level2HeadBg: '#f0fdfa',
      level2HeadBorder: '#99f6e4',
      level2HeadText: '#0f766e',
      rowHoverBg: '#f0fdfa',
      rowSelectedBg: '#ccfbf1',
      rowSelectedHoverBg: '#99f6e4',
    }),
  },
  orange: {
    primary: {
      main: '#ea580c',
      dark: '#c2410c',
      light: '#fb923c',
      surface: '#fff7ed',
      surfaceBorder: '#fed7aa',
      hover: 'rgba(234, 88, 12, 0.08)',
    },
    shell: {
      headerSolid: '#ea580c',
      sidebarActiveBg: '#fff7ed',
      sidebarActiveBgHover: '#ffedd5',
      sidebarActiveText: '#c2410c',
      sidebarActiveIcon: '#ea580c',
      sidebarActiveBar: '#ea580c',
      sidebarSectionText: '#9a3412',
    },
    table: tableTone({
      headBg: '#fceee4',
      headBorder: '#f0d9c8',
      headText: '#333333',
      level2HeadBg: '#fff7ed',
      level2HeadBorder: '#fed7aa',
      level2HeadText: '#c2410c',
      rowHoverBg: '#faf8f5',
      rowSelectedBg: '#ffedd5',
      rowSelectedHoverBg: '#fed7aa',
    }),
  },
  magenta: {
    primary: {
      main: '#db2777',
      dark: '#be185d',
      light: '#f472b6',
      surface: '#fdf2f8',
      surfaceBorder: '#fbcfe8',
      hover: 'rgba(219, 39, 119, 0.08)',
    },
    shell: {
      headerSolid: '#db2777',
      sidebarActiveBg: '#fdf2f8',
      sidebarActiveBgHover: '#fce7f3',
      sidebarActiveText: '#be185d',
      sidebarActiveIcon: '#db2777',
      sidebarActiveBar: '#db2777',
      sidebarSectionText: '#9d174d',
    },
    table: tableTone({
      headBg: '#fae8f1',
      headBorder: '#efd0df',
      headText: '#333333',
      level2HeadBg: '#fdf2f8',
      level2HeadBorder: '#fbcfe8',
      level2HeadText: '#be185d',
      rowHoverBg: '#faf7f9',
      rowSelectedBg: '#fce7f3',
      rowSelectedHoverBg: '#fbcfe8',
    }),
  },
}

export type ThemePresetId = 'blue' | 'slate' | 'green' | 'purple' | 'coral' | 'sage' | 'teal' | 'orange' | 'magenta'

export const DEFAULT_THEME_PRESET_ID: ThemePresetId = 'blue'

export const THEMES_BY_ID: Record<ThemePresetId, UiPalette> = {
  blue: PALETTE_BLUE,
  slate: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.slate),
  green: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.green),
  purple: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.purple),
  coral: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.coral),
  sage: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.sage),
  teal: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.teal),
  orange: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.orange),
  magenta: mergePalette(PALETTE_BLUE, PRESET_PARTIALS.magenta),
}

/** Thứ tự lưới 3×3 — khớp banhang ACCENT_PRESETS */
export const THEME_PRESET_GRID: ThemePresetId[][] = [
  ['blue', 'slate', 'green'],
  ['purple', 'coral', 'sage'],
  ['teal', 'orange', 'magenta'],
]

export const READABLE_THEME_IDS = new Set<string>(Object.keys(THEMES_BY_ID))

export const readStoredThemePresetId = (): ThemePresetId => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return DEFAULT_THEME_PRESET_ID
  try {
    const raw = window.localStorage.getItem(THEME_PRESET_STORAGE_KEY)
    if (!raw || !READABLE_THEME_IDS.has(raw)) return DEFAULT_THEME_PRESET_ID
    return raw as ThemePresetId
  } catch {
    return DEFAULT_THEME_PRESET_ID
  }
}

export const persistThemePresetId = (id: ThemePresetId): void => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return
  try {
    window.localStorage.setItem(THEME_PRESET_STORAGE_KEY, id)
  } catch {
    /** bỏ qua quota / private mode */
  }
}

/** Header bảng + hover dòng theo đúng công thức accent của banhang. */
const applyDerivedListTheme = (palette: UiPalette): UiPalette => {
  const next = JSON.parse(JSON.stringify(palette)) as UiPalette
  const d = deriveAccentFromMain(next.primary.main)
  next.table.headBg = d.tableHeadBg
  next.table.headBorder = d.tableHeadBorder
  next.table.headText = d.tableHeadText
  next.table.wrapperBorder = d.tableWrapperBorder
  next.table.rowHoverBg = d.rowHoverBg
  next.table.rowSelectedBg = d.rowSelectedBg
  next.table.rowSelectedHoverBg = d.rowSelectedHoverBg
  next.table.level2HeadBg = d.tableLevel2HeadBg
  next.table.level2HeadBorder = d.tableLevel2HeadBorder
  next.table.level2HeadText = d.tableHeadText
  next.table.nestedHeadBg = d.tableLevel2HeadBg
  next.table.nestedHeadBorder = d.tableLevel2HeadBorder
  next.table.nestedHeadText = d.tableHeadText
  return next
}

export const presetPaletteFromId = (id: ThemePresetId | string): UiPalette =>
  applyDerivedListTheme(THEMES_BY_ID[id as ThemePresetId] ?? THEMES_BY_ID[DEFAULT_THEME_PRESET_ID])
